import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Divider,
  Alert, Snackbar, Tooltip, Switch, FormControlLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, Collapse,
} from '@mui/material';
import {
  ArrowBack, Add, DeleteOutline, Save, Cancel, VerifiedUser, BookmarkAdd,
  CheckCircle, WarningAmber, Balance, Visibility, AttachFile, UploadFile,
  NoteAdd, InsertDriveFileOutlined, Close, TrendingUp, TrendingDown,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { projectService } from '../../services/projectService';
import { formatCurrency, formatAmount } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import AccountSelect from '../../components/finance/AccountSelect';

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------

const JOURNAL_TYPES = [
  'General Journal', 'Adjustment', 'Accrual', 'Provision', 'Bank', 'Cash', 'Treasury',
  'Purchase', 'Sales', 'Payroll', 'GST', 'TDS', 'Inventory', 'Production',
  'Depreciation', 'Asset', 'Intercompany', 'Reversal',
];

// value maps to the source_type column postingService/journalWorkspace resolves.
const SOURCES: { label: string; value: string }[] = [
  { label: 'Manual', value: 'MANUAL' },
  { label: 'Purchase Invoice', value: 'PURCHASE_INVOICE' },
  { label: 'Sales Invoice', value: 'SALES_INVOICE' },
  { label: 'Payment', value: 'VENDOR_PAYMENT' },
  { label: 'Receipt', value: 'RECEIPT' },
  { label: 'GRN', value: 'GOODS_RECEIPT' },
  { label: 'Production', value: 'FG_RECEIPT' },
  { label: 'Inventory Adjustment', value: 'STOCK_ADJUSTMENT' },
  { label: 'Payroll', value: 'PAYROLL' },
  { label: 'Tax', value: 'TAX' },
  { label: 'Asset', value: 'ASSET' },
  { label: 'Treasury', value: 'TREASURY' },
  { label: 'Other', value: 'OTHER' },
];

const TAX_CODES = ['', 'GST-0', 'GST-5', 'GST-12', 'GST-18', 'GST-28', 'IGST-18', 'TDS-194C', 'TDS-194J', 'EXEMPT'];

interface Line {
  accountId: string;
  debit: string;
  credit: string;
  costCenterId: string;
  profitCenterId: string;
  projectId: string;
  taxCode: string;
  narration: string;
}

const blankLine = (): Line => ({
  accountId: '', debit: '', credit: '', costCenterId: '', profitCenterId: '',
  projectId: '', taxCode: '', narration: '',
});

interface AttachmentFile { name: string; size: number; type: string }

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: string) => Number(v) || 0;

const periodLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const JournalEntryNewPage: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();

  const [accounts, setAccounts] = useState<any[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const [profitCenters, setProfitCenters] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);

  const today = new Date().toISOString().slice(0, 10);
  const [header, setHeader] = useState({
    journalType: 'General Journal',
    entryDate: today,
    postingDate: today,
    company: 'Apex Industrial Components Pvt. Ltd.',
    plant: '',
    currency: 'INR',
    exchangeRate: '1.0000',
    reference: '',
    source: 'MANUAL',
    sourceReference: '',
    description: '',
    costCenterId: '',
    profitCenterId: '',
    projectId: '',
    narration: '',
  });
  const [lines, setLines] = useState<Line[]>([blankLine(), blankLine()]);
  const [showLineNarration, setShowLineNarration] = useState(false);

  const [requiresApproval, setRequiresApproval] = useState(false);
  const [notifyApprovers, setNotifyApprovers] = useState(true);
  const [makeRecurring, setMakeRecurring] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');

  useEffect(() => {
    financeService.getAccounts().then((r: any) => setAccounts(r.accounts || [])).catch(() => {});
    financeWorkspaceService.listCostCenters().then((r) => setCostCenters(r.costCenters || [])).catch(() => {});
    financeWorkspaceService.listProfitCenters().then((r) => setProfitCenters(r.profitCenters || [])).catch(() => {});
    financeWorkspaceService.listTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
    projectService.getProjects().then((r: any) => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
  }, []);

  const setField = (patch: Partial<typeof header>) => setHeader((h) => ({ ...h, ...patch }));
  const setLine = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls));

  // One-click: fill the outstanding difference onto the correct side. If a line
  // already has an account but no amount, use it; otherwise append a fresh
  // balancing line for the user to assign an account to.
  const fillBalance = () => {
    const diff = round2(lines.reduce((s, l) => s + num(l.debit), 0) - lines.reduce((s, l) => s + num(l.credit), 0));
    if (Math.abs(diff) < 0.01) return;
    const amt = String(Math.abs(diff));
    const side: 'debit' | 'credit' = diff > 0 ? 'credit' : 'debit'; // debit-heavy → add credit
    const idx = lines.findIndex((l) => l.accountId && !num(l.debit) && !num(l.credit));
    if (idx >= 0) {
      setLine(idx, side === 'credit' ? { credit: amt, debit: '' } : { debit: amt, credit: '' });
    } else {
      setLines((ls) => [...ls, { ...blankLine(), [side]: amt }]);
    }
  };

  const accountById = (id: string) => accounts.find((a) => String(a.id) === String(id));

  // --- live totals ------------------------------------------------------------
  const totals = useMemo(() => {
    const dr = round2(lines.reduce((s, l) => s + num(l.debit), 0));
    const cr = round2(lines.reduce((s, l) => s + num(l.credit), 0));
    const activeLines = lines.filter((l) => l.accountId && (num(l.debit) > 0 || num(l.credit) > 0));
    const debitAccounts = new Set(activeLines.filter((l) => num(l.debit) > 0).map((l) => l.accountId)).size;
    const creditAccounts = new Set(activeLines.filter((l) => num(l.credit) > 0).map((l) => l.accountId)).size;
    const impacted = new Set(activeLines.map((l) => l.accountId)).size;
    return { dr, cr, diff: round2(dr - cr), lines: activeLines.length, debitAccounts, creditAccounts, impacted };
  }, [lines]);

  const balanced = Math.abs(totals.diff) < 0.01 && totals.dr > 0;

  // --- attachments ------------------------------------------------------------
  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files).map((f) => ({ name: f.name, size: f.size, type: f.type || 'file' }));
    setAttachments((a) => [...a, ...next]);
  };

  // --- validation -------------------------------------------------------------
  const validate = (forPost: boolean): boolean => {
    const fe: Record<string, string> = {};
    if (!header.journalType) fe.journalType = 'Required';
    if (!header.entryDate) fe.entryDate = 'Required';
    if (!header.postingDate) fe.postingDate = 'Required';
    if (!header.company.trim()) fe.company = 'Required';
    if (!header.currency) fe.currency = 'Required';
    if (!header.description.trim()) fe.description = 'Description is required';
    const active = lines.filter((l) => l.accountId && (num(l.debit) > 0 || num(l.credit) > 0));
    if (active.length < 2) fe.lines = 'At least two account lines with amounts are required';
    lines.forEach((l, i) => {
      if ((num(l.debit) > 0 || num(l.credit) > 0) && !l.accountId) fe[`line-${i}`] = 'Select an account';
      if (num(l.debit) > 0 && num(l.credit) > 0) fe[`line-${i}`] = 'A line cannot be both debit and credit';
    });
    if (forPost && !balanced) fe.balance = `Difference ${formatCurrency(Math.abs(totals.diff))} — balance the journal before posting`;
    setFieldErrors(fe);
    setError(Object.keys(fe).length ? (fe.balance || fe.description || fe.lines || 'Please correct the highlighted fields') : null);
    return Object.keys(fe).length === 0;
  };

  // --- submit -----------------------------------------------------------------
  const buildPayload = (status: 'DRAFT' | 'POSTED') => ({
    entryDate: header.entryDate,
    postingDate: header.postingDate,
    description: header.description,
    reference: header.reference || undefined,
    journalType: header.journalType,
    sourceType: header.source,
    sourceReference: header.sourceReference || undefined,
    company: header.company || undefined,
    plant: header.plant || undefined,
    currency: header.currency,
    exchangeRate: Number(header.exchangeRate) || 1,
    costCenterId: header.costCenterId ? Number(header.costCenterId) : undefined,
    profitCenterId: header.profitCenterId ? Number(header.profitCenterId) : undefined,
    projectId: header.projectId ? Number(header.projectId) : undefined,
    narration: header.narration || undefined,
    status,
    entries: lines
      .filter((l) => l.accountId && (num(l.debit) > 0 || num(l.credit) > 0))
      .map((l) => ({
        accountId: Number(l.accountId),
        description: l.narration || header.description,
        debitAmount: num(l.debit),
        creditAmount: num(l.credit),
        costCenterId: l.costCenterId ? Number(l.costCenterId) : (header.costCenterId ? Number(header.costCenterId) : undefined),
        profitCenterId: l.profitCenterId ? Number(l.profitCenterId) : (header.profitCenterId ? Number(header.profitCenterId) : undefined),
        projectId: l.projectId ? Number(l.projectId) : (header.projectId ? Number(header.projectId) : undefined),
        taxCode: l.taxCode || undefined,
        narration: l.narration || undefined,
      })),
  });

  const submit = async (intent: 'DRAFT' | 'POST') => {
    const forPost = intent === 'POST';
    if (!validate(forPost)) return;
    setSaving(true);
    try {
      const status = forPost && !requiresApproval ? 'POSTED' : 'DRAFT';
      const je = await financeService.createJournalEntry(buildPayload(status) as any);
      const num_ = (je as any).entry_number || (je as any).entryNumber || '';
      if (forPost && requiresApproval) {
        setToast(`Journal ${num_} created as draft — submitted for approval`);
      } else {
        setToast(`Journal ${status === 'POSTED' ? 'posted' : 'saved as draft'}: ${num_}`);
      }
      if (attachments.length) {
        // Attachments are captured in the UI; document persistence is a follow-up
        // (documents API) — never claimed as saved here.
      }
      setTimeout(() => navigate('/finance/journal'), 800);
    } catch (e: any) {
      setError(e.message || 'Failed to save journal');
    } finally {
      setSaving(false);
    }
  };

  // --- templates --------------------------------------------------------------
  const applyTemplate = async (id: number) => {
    try {
      const { template } = await financeWorkspaceService.getTemplate(id);
      const tplLines = template?.lines || template?.entries || [];
      if (tplLines.length) {
        setLines(tplLines.map((tl: any) => ({
          ...blankLine(),
          accountId: String(tl.account_id || tl.accountId || ''),
          debit: tl.debit_amount ? String(tl.debit_amount) : '',
          credit: tl.credit_amount ? String(tl.credit_amount) : '',
          costCenterId: tl.cost_center_id ? String(tl.cost_center_id) : '',
          narration: tl.narration || tl.description || '',
        })));
      }
      setField({
        journalType: template?.journal_type || header.journalType,
        description: template?.description || header.description,
      });
      setToast(`Template applied: ${template?.name || ''}`);
    } catch (e: any) {
      setError(e.message || 'Could not load template');
    }
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    try {
      await financeWorkspaceService.createTemplate({
        name: tplName.trim(),
        journalType: header.journalType,
        description: header.description,
        lines: lines
          .filter((l) => l.accountId)
          .map((l) => ({
            accountId: Number(l.accountId),
            debitAmount: num(l.debit),
            creditAmount: num(l.credit),
            costCenterId: l.costCenterId ? Number(l.costCenterId) : undefined,
            narration: l.narration || undefined,
          })),
      });
      setSaveTplOpen(false);
      setTplName('');
      setToast('Template saved');
      financeWorkspaceService.listTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
    } catch (e: any) {
      setError(e.message || 'Could not save template');
    }
  };

  // ---------------------------------------------------------------------------

  const line = { border: `1px solid ${c.line}` };
  const panelSx = { borderRadius: 3, border: line.border, bgcolor: c.panel } as const;
  const sectionTitleSx = { fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.01em' } as const;
  const labelSx = { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: c.sub, mb: 0.5 };

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={labelSx}>{text}{required && <Box component="span" sx={{ color: c.bad, ml: 0.25 }}>*</Box>}</Typography>
  );

  const showProject = header.journalType !== 'Payroll' && header.journalType !== 'Depreciation';

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%', pb: 6 }}>
      {/* ---------------------------------------------------------------- header bar */}
      {/* Sticky offset = AppBar height (56/64) MINUS the main scroll
          container's top padding (AppLayout main has p:3 = 24px). Chrome adds
          the scroller's padding-top to a sticky element's parked position, so
          without the −24 the bar lands ~24px too low and page content bleeds
          through the gap under the fixed AppBar. z-index sits just below the
          AppBar; opaque paper background + a soft shadow read it as one layer. */}
      <Box sx={{
        position: 'sticky', top: { xs: 32, sm: 40 }, zIndex: (t) => t.zIndex.appBar - 1,
        backgroundColor: 'background.paper', borderBottom: line.border, px: 3, py: 1.75,
        boxShadow: '0 4px 12px rgba(2,6,23,0.05)',
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton size="small" onClick={() => navigate('/finance/journal')} sx={{ border: line.border }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>New Journal Entry</Typography>
                <Chip label="Draft" size="small" sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: `${c.sub}1f`, color: c.sub }} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Create a balanced double-entry transaction with live validation and accounting preview.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button color="inherit" startIcon={<Cancel />} onClick={() => navigate('/finance/journal')}>Cancel</Button>
            <Button variant="outlined" startIcon={<Save />} disabled={saving} onClick={() => submit('DRAFT')}>Save Draft</Button>
            <Button variant="outlined" startIcon={<BookmarkAdd />} disabled={saving} onClick={() => setSaveTplOpen(true)}>Save as Template</Button>
            <Tooltip
              disableHoverListener={balanced}
              title={!balanced && totals.dr > 0
                ? `Difference ${formatCurrency(Math.abs(totals.diff))} — balance the journal before posting`
                : (totals.dr === 0 ? 'Enter debit and credit amounts to post' : '')}
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<VerifiedUser />}
                  disabled={saving || !balanced}
                  onClick={() => submit('POST')}
                >
                  {requiresApproval ? 'Submit for Approval' : 'Review & Post'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pt: 2.5 }}>
        {error && <Alert severity={fieldErrors.balance ? 'warning' : 'error'} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {templates.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" sx={{ ...labelSx, mb: 0 }}>Start from template</Typography>
            {templates.slice(0, 8).map((t) => (
              <Chip key={t.id} label={t.name} size="small" variant="outlined" clickable
                onClick={() => applyTemplate(t.id)} icon={<NoteAdd sx={{ fontSize: 15 }} />}
                sx={{ borderColor: c.line }} />
            ))}
          </Stack>
        )}

        <Grid container spacing={2.5}>
          {/* ============================================================ MAIN */}
          <Grid size={{ xs: 12, lg: 8.5 }}>
            {/* ---- journal header ---- */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Journal Type" required />
                  <TextField select fullWidth size="small" value={header.journalType}
                    error={!!fieldErrors.journalType}
                    onChange={(e) => setField({ journalType: e.target.value })}>
                    {JOURNAL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Entry Date" required />
                  <TextField type="date" fullWidth size="small" value={header.entryDate}
                    error={!!fieldErrors.entryDate} InputLabelProps={{ shrink: true }}
                    onChange={(e) => setField({ entryDate: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Posting Date" required />
                  <TextField type="date" fullWidth size="small" value={header.postingDate}
                    error={!!fieldErrors.postingDate} InputLabelProps={{ shrink: true }}
                    onChange={(e) => setField({ postingDate: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Accounting Period" />
                  <TextField fullWidth size="small" value={periodLabel(header.postingDate)} disabled
                    InputProps={{
                      endAdornment: <Chip label="Open" size="small"
                        sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700, bgcolor: `${c.good}1f`, color: c.good }} />,
                    }} />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Company" required />
                  <TextField fullWidth size="small" value={header.company} error={!!fieldErrors.company}
                    onChange={(e) => setField({ company: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Plant / Location" />
                  <TextField fullWidth size="small" placeholder="Optional" value={header.plant}
                    onChange={(e) => setField({ plant: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Currency" required />
                  <TextField select fullWidth size="small" value={header.currency}
                    onChange={(e) => setField({ currency: e.target.value })}>
                    <MenuItem value="INR">INR — Indian Rupee</MenuItem>
                    <MenuItem value="USD">USD — US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR — Euro</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Exchange Rate" />
                  <TextField fullWidth size="small" type="number" value={header.exchangeRate}
                    disabled={header.currency === 'INR'}
                    onChange={(e) => setField({ exchangeRate: e.target.value })} />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Reference" />
                  <TextField fullWidth size="small" placeholder="Optional reference number" value={header.reference}
                    onChange={(e) => setField({ reference: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FieldLabel text="Source" />
                  <TextField select fullWidth size="small" value={header.source}
                    onChange={(e) => setField({ source: e.target.value })}>
                    {SOURCES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </TextField>
                </Grid>
                {header.source !== 'MANUAL' ? (
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <FieldLabel text="Source Document" />
                    <TextField fullWidth size="small" value={header.sourceReference}
                      placeholder="e.g. PAY00043 — link to originating transaction"
                      onChange={(e) => setField({ sourceReference: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><InsertDriveFileOutlined sx={{ fontSize: 17, color: c.sub }} /></InputAdornment> }} />
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <FieldLabel text="Description" required />
                    <TextField fullWidth size="small" value={header.description} error={!!fieldErrors.description}
                      placeholder="What is this entry for?" onChange={(e) => setField({ description: e.target.value })} />
                  </Grid>
                )}
                {header.source !== 'MANUAL' && (
                  <Grid size={{ xs: 12 }}>
                    <FieldLabel text="Description" required />
                    <TextField fullWidth size="small" value={header.description} error={!!fieldErrors.description}
                      placeholder="What is this entry for?" onChange={(e) => setField({ description: e.target.value })} />
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* ---- accounting lines ---- */}
            <Paper elevation={0} sx={{ ...panelSx, mb: 2.5, overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={sectionTitleSx}>Accounting Lines</Typography>
                  <Chip label={totals.lines} size="small" sx={{ height: 20, minWidth: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: `${c.accent}1f`, color: c.accent }} />
                </Stack>
                <Button size="small" startIcon={<Add />} onClick={addLine}>Add Row</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, display: 'block', mt: -1, mb: 1 }}>
                Enter debit and credit amounts. Total debit must equal total credit.
              </Typography>

              <TableContainer>
                <Table size="small" sx={{ '& td, & th': { borderColor: c.line } }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.6875rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: c.sub, bgcolor: c.panel2, py: 1.25 } }}>
                      <TableCell width={36} align="center">#</TableCell>
                      <TableCell sx={{ minWidth: 210 }}>Account</TableCell>
                      <TableCell width={160} align="right">Debit (₹)</TableCell>
                      <TableCell width={160} align="right">Credit (₹)</TableCell>
                      <TableCell width={150}>Cost / Profit Center</TableCell>
                      <TableCell width={150}>Project / Tax Code</TableCell>
                      <TableCell width={44} align="center">·</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((l, i) => {
                      const acct = accountById(l.accountId);
                      return (
                        <React.Fragment key={i}>
                          <TableRow sx={{ verticalAlign: 'top', '& td': { py: 1 }, ...(fieldErrors[`line-${i}`] ? { bgcolor: `${c.bad}0d` } : {}) }}>
                            <TableCell align="center" sx={{ color: c.sub, fontWeight: 700, pt: 2 }}>{i + 1}</TableCell>
                            <TableCell>
                              <AccountSelect
                                accounts={accounts}
                                value={l.accountId}
                                onChange={(accountId) => setLine(i, { accountId })}
                                error={!!fieldErrors[`line-${i}`]}
                                helperText={fieldErrors[`line-${i}`] || (acct ? `${acct.account_type}${typeof acct.current_balance !== 'undefined' ? ` · Bal ${formatAmount(Number(acct.current_balance) || 0)}` : ''}` : undefined)}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth size="small" type="number" value={l.debit}
                                placeholder="0.00"
                                inputProps={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.8125rem', color: c.sub } }}>₹</InputAdornment> }}
                                sx={{ minWidth: 140 }}
                                onChange={(e) => setLine(i, { debit: e.target.value, credit: '' })} />
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth size="small" type="number" value={l.credit}
                                placeholder="0.00"
                                inputProps={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.8125rem', color: c.sub } }}>₹</InputAdornment> }}
                                sx={{ minWidth: 140 }}
                                onChange={(e) => setLine(i, { credit: e.target.value, debit: '' })} />
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.75}>
                                <TextField select fullWidth size="small" value={l.costCenterId}
                                  onChange={(e) => setLine(i, { costCenterId: e.target.value })}
                                  SelectProps={{ displayEmpty: true }}>
                                  <MenuItem value=""><em>Cost center</em></MenuItem>
                                  {costCenters.map((cc) => <MenuItem key={cc.id} value={cc.id}>{cc.code} — {cc.name}</MenuItem>)}
                                </TextField>
                                <TextField select fullWidth size="small" value={l.profitCenterId}
                                  onChange={(e) => setLine(i, { profitCenterId: e.target.value })}
                                  SelectProps={{ displayEmpty: true }}>
                                  <MenuItem value=""><em>Profit center</em></MenuItem>
                                  {profitCenters.map((pc) => <MenuItem key={pc.id} value={pc.id}>{pc.code} — {pc.name}</MenuItem>)}
                                </TextField>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.75}>
                                {showProject ? (
                                  <TextField select fullWidth size="small" value={l.projectId}
                                    onChange={(e) => setLine(i, { projectId: e.target.value })}
                                    SelectProps={{ displayEmpty: true }}>
                                    <MenuItem value=""><em>Project</em></MenuItem>
                                    {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.project_code || p.code || p.name}</MenuItem>)}
                                  </TextField>
                                ) : <Box sx={{ height: 40 }} />}
                                <TextField select fullWidth size="small" value={l.taxCode}
                                  onChange={(e) => setLine(i, { taxCode: e.target.value })}
                                  SelectProps={{ displayEmpty: true }}>
                                  {TAX_CODES.map((tc) => <MenuItem key={tc} value={tc}>{tc || <em>Tax code</em>}</MenuItem>)}
                                </TextField>
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={{ pt: 1.5 }}>
                              <IconButton size="small" onClick={() => removeLine(i)} disabled={lines.length <= 2}>
                                <DeleteOutline fontSize="small" sx={{ color: lines.length <= 2 ? c.line : c.bad }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {showLineNarration && (
                            <TableRow>
                              <TableCell />
                              <TableCell colSpan={6} sx={{ pt: 0 }}>
                                <TextField fullWidth size="small" placeholder={`Line ${i + 1} narration`} value={l.narration}
                                  onChange={(e) => setLine(i, { narration: e.target.value })}
                                  InputProps={{ startAdornment: <InputAdornment position="start"><NoteAdd sx={{ fontSize: 16, color: c.sub }} /></InputAdornment> }} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.25, flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" startIcon={<NoteAdd />} onClick={() => setShowLineNarration((s) => !s)}>
                  {showLineNarration ? 'Hide line narration' : 'Add narration to line items'}
                </Button>
                {!balanced && totals.dr > 0 && (
                  <Button size="small" startIcon={<Balance />} onClick={fillBalance} sx={{ color: c.warn }}>
                    Auto-balance {formatCurrency(Math.abs(totals.diff))} on {totals.diff > 0 ? 'credit' : 'debit'}
                  </Button>
                )}
              </Stack>

              {/* totals strip */}
              <Grid container sx={{ borderTop: line.border, bgcolor: c.panel2 }}>
                <Grid size={4} sx={{ p: 2, borderRight: line.border }}>
                  <Typography sx={labelSx}>Total Debit</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums', color: c.text }}>{formatCurrency(totals.dr)}</Typography>
                </Grid>
                <Grid size={4} sx={{ p: 2, borderRight: line.border }}>
                  <Typography sx={labelSx}>Total Credit</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums', color: c.text }}>{formatCurrency(totals.cr)}</Typography>
                </Grid>
                <Grid size={4} sx={{ p: 2 }}>
                  <Typography sx={labelSx}>Difference</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums', color: balanced ? c.good : (totals.dr === 0 ? c.sub : c.bad) }}>
                      {formatCurrency(totals.diff)}
                    </Typography>
                    {balanced
                      ? <Chip icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="Balanced" size="small" sx={{ height: 22, fontWeight: 700, bgcolor: `${c.good}1f`, color: c.good }} />
                      : totals.dr > 0 && <Chip icon={<WarningAmber sx={{ fontSize: '16px !important' }} />} label="Unbalanced" size="small" sx={{ height: 22, fontWeight: 700, bgcolor: `${c.bad}1f`, color: c.bad }} />}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* ---- additional info + approval ---- */}
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Additional Information</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel text="Cost Center (Header)" />
                      <TextField select fullWidth size="small" value={header.costCenterId}
                        onChange={(e) => setField({ costCenterId: e.target.value })} SelectProps={{ displayEmpty: true }}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {costCenters.map((cc) => <MenuItem key={cc.id} value={cc.id}>{cc.code} — {cc.name}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel text="Profit Center (Header)" />
                      <TextField select fullWidth size="small" value={header.profitCenterId}
                        onChange={(e) => setField({ profitCenterId: e.target.value })} SelectProps={{ displayEmpty: true }}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {profitCenters.map((pc) => <MenuItem key={pc.id} value={pc.id}>{pc.code} — {pc.name}</MenuItem>)}
                      </TextField>
                    </Grid>
                    {showProject && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FieldLabel text="Project (Header)" />
                        <TextField select fullWidth size="small" value={header.projectId}
                          onChange={(e) => setField({ projectId: e.target.value })} SelectProps={{ displayEmpty: true }}>
                          <MenuItem value=""><em>None</em></MenuItem>
                          {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.project_code || p.code || p.name}</MenuItem>)}
                        </TextField>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                      <FieldLabel text="Narration" />
                      <TextField fullWidth size="small" multiline minRows={2} value={header.narration}
                        placeholder="Full narration for this journal entry…"
                        onChange={(e) => setField({ narration: e.target.value })} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 1.5 }}>Approval &amp; Settings</Typography>
                  <Stack spacing={0.5} divider={<Divider sx={{ borderColor: c.line }} />}>
                    <FormControlLabel sx={{ justifyContent: 'space-between', ml: 0 }} labelPlacement="start"
                      control={<Switch checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />}
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Requires Approval</Typography>} />
                    <Collapse in={requiresApproval}>
                      <Box sx={{ py: 1.5 }}>
                        <FieldLabel text="Approval Workflow" />
                        <TextField select fullWidth size="small" defaultValue="finance-l1">
                          <MenuItem value="finance-l1">Finance Approval — Level 1</MenuItem>
                          <MenuItem value="finance-l2">Finance Approval — Level 2 (Controller)</MenuItem>
                          <MenuItem value="cfo">CFO Approval</MenuItem>
                        </TextField>
                        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                          <Box><Typography sx={labelSx}>Approval Level</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>Level 1</Typography></Box>
                          <Box><Typography sx={labelSx}>SLA</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>24 hours</Typography></Box>
                        </Stack>
                      </Box>
                    </Collapse>
                    <FormControlLabel sx={{ justifyContent: 'space-between', ml: 0 }} labelPlacement="start"
                      control={<Switch checked={notifyApprovers} onChange={(e) => setNotifyApprovers(e.target.checked)} />}
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Notify Approvers</Typography>} />
                    <FormControlLabel sx={{ justifyContent: 'space-between', ml: 0 }} labelPlacement="start"
                      control={<Switch checked={makeRecurring} onChange={(e) => setMakeRecurring(e.target.checked)} />}
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Make Recurring</Typography>} />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* ============================================================ RIGHT RAIL */}
          <Grid size={{ xs: 12, lg: 3.5 }}>
            <Box sx={{ position: { lg: 'sticky' }, top: 148 }}>
              {/* balance summary */}
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Balance sx={{ fontSize: 20, color: c.accent }} />
                  <Typography sx={sectionTitleSx}>Balance Summary</Typography>
                </Stack>
                <Stack spacing={1.25}>
                  <SummaryRow c={c} label="Total Debit" value={formatCurrency(totals.dr)} />
                  <SummaryRow c={c} label="Total Credit" value={formatCurrency(totals.cr)} />
                  <Divider sx={{ borderColor: c.line }} />
                  <SummaryRow c={c} label="Difference" bold
                    value={formatCurrency(totals.diff)} valueColor={balanced ? c.good : (totals.dr === 0 ? c.text : c.bad)} />
                </Stack>
                <Box sx={{ mt: 2 }}>
                  {balanced ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, borderRadius: 2, bgcolor: `${c.good}14` }}>
                      <CheckCircle sx={{ fontSize: 20, color: c.good }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: c.good }}>Balanced — ready to post</Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, borderRadius: 2, bgcolor: totals.dr === 0 ? `${c.sub}14` : `${c.bad}14` }}>
                      <WarningAmber sx={{ fontSize: 20, color: totals.dr === 0 ? c.sub : c.bad }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: totals.dr === 0 ? c.sub : c.bad }}>
                        {totals.dr === 0 ? 'Enter amounts' : `Difference ${formatCurrency(Math.abs(totals.diff))}`}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid size={4}><MiniStat c={c} label="Lines" value={totals.lines} /></Grid>
                  <Grid size={4}><MiniStat c={c} label="Debit A/cs" value={totals.debitAccounts} /></Grid>
                  <Grid size={4}><MiniStat c={c} label="Credit A/cs" value={totals.creditAccounts} /></Grid>
                </Grid>
              </Paper>

              {/* accounting preview */}
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Visibility sx={{ fontSize: 20, color: c.accent }} />
                  <Typography sx={sectionTitleSx}>Accounting Preview</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  This is how the entry will affect your books.
                </Typography>
                <Stack spacing={1.25}>
                  <SummaryRow c={c} label="Accounts Impacted" value={String(totals.impacted)} />
                  <SummaryRow c={c} label="Debit Accounts" value={String(totals.debitAccounts)} />
                  <SummaryRow c={c} label="Credit Accounts" value={String(totals.creditAccounts)} />
                  <SummaryRow c={c} label="Posting Status" value={balanced ? 'Will be Posted' : 'Not ready'}
                    valueColor={balanced ? c.good : c.sub} bold />
                </Stack>
                <Button fullWidth variant="outlined" startIcon={<TrendingUp />} sx={{ mt: 2 }}
                  disabled={totals.lines === 0} onClick={() => setPreviewOpen(true)}>
                  Preview Accounting Impact
                </Button>
              </Paper>

              {/* attachments */}
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <AttachFile sx={{ fontSize: 20, color: c.accent }} />
                  <Typography sx={sectionTitleSx}>Attachments</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Attach supporting documents.
                </Typography>
                <input ref={fileInput} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
                <Box
                  onClick={() => fileInput.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                  sx={{
                    border: `1.5px dashed ${dragOver ? c.accent : c.line}`, borderRadius: 2,
                    p: 3, textAlign: 'center', cursor: 'pointer',
                    bgcolor: dragOver ? `${c.accent}0d` : 'transparent', transition: 'all .15s',
                  }}
                >
                  <UploadFile sx={{ fontSize: 28, color: c.sub, mb: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Drag &amp; drop files here</Typography>
                  <Typography variant="caption" sx={{ color: c.accent }}>or click to browse</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Max 10MB each · PDF, JPG, PNG, DOCX
                  </Typography>
                </Box>
                {attachments.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                    No attachments yet
                  </Typography>
                ) : (
                  <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    {attachments.map((f, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ p: 1, borderRadius: 1.5, border: line.border }}>
                        <InsertDriveFileOutlined sx={{ fontSize: 18, color: c.sub }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{(f.size / 1024).toFixed(0)} KB</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setAttachments((a) => a.filter((_, idx) => idx !== i))}><Close sx={{ fontSize: 16 }} /></IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* -------------------------------------------------- accounting impact dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Accounting Impact
          <Typography variant="body2" color="text.secondary">Net effect of this journal on each account.</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {lines.filter((l) => l.accountId && (num(l.debit) > 0 || num(l.credit) > 0)).map((l, i) => {
              const acct = accountById(l.accountId);
              const isDebit = num(l.debit) > 0;
              const amt = isDebit ? num(l.debit) : num(l.credit);
              return (
                <Stack key={i} direction="row" alignItems="center" justifyContent="space-between"
                  sx={{ p: 1.5, borderRadius: 2, border: line.border }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      <Box component="span" sx={{ fontFamily: 'monospace', color: c.accent, mr: 1 }}>{acct?.account_code}</Box>
                      {acct?.account_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{acct?.account_type}{l.narration ? ` · ${l.narration}` : ''}</Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    {isDebit ? <TrendingUp sx={{ fontSize: 18, color: c.good }} /> : <TrendingDown sx={{ fontSize: 18, color: c.bad }} />}
                    <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: isDebit ? c.good : c.bad }}>
                      {isDebit ? '+' : '−'}{formatAmount(amt)}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
          <Divider sx={{ my: 2, borderColor: c.line }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Net movement</Typography>
            <Chip label={balanced ? 'Balanced · ₹0.00' : `Unbalanced · ${formatCurrency(Math.abs(totals.diff))}`}
              size="small" sx={{ fontWeight: 700, bgcolor: balanced ? `${c.good}1f` : `${c.bad}1f`, color: balanced ? c.good : c.bad }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------- save-as-template dialog */}
      <Dialog open={saveTplOpen} onClose={() => setSaveTplOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Save as Template</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the accounts and dimensions of this entry as a reusable template. Amounts are not stored.
          </Typography>
          <FieldLabel text="Template Name" required />
          <TextField fullWidth size="small" autoFocus value={tplName} placeholder="e.g. Monthly Rent Accrual"
            onChange={(e) => setTplName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTplOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!tplName.trim()} onClick={saveTemplate}>Save Template</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const SummaryRow: React.FC<{ c: any; label: string; value: string; bold?: boolean; valueColor?: string }> = ({ c, label, value, bold, valueColor }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 800 : 600, fontVariantNumeric: 'tabular-nums', color: valueColor || c.text }}>{value}</Typography>
  </Stack>
);

const MiniStat: React.FC<{ c: any; label: string; value: number | string }> = ({ c, label, value }) => (
  <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: c.panel2 }}>
    <Typography sx={{ fontWeight: 800, fontSize: '1.125rem', lineHeight: 1.1, color: c.text }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>{label}</Typography>
  </Box>
);

export default JournalEntryNewPage;
